import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import Phaser from 'phaser';

export interface HeadPose {
  yaw: number;
  pitch: number;
  roll: number;
  confidence: number;
}

export interface HeadAction {
  leanLeft: boolean;
  leanRight: boolean;
  nod: boolean;
  lookUp: boolean;
  lookDown: boolean;
}

const YAW_THRESHOLD = 8;
const PITCH_UP_THRESHOLD = 15;
const PITCH_DOWN_THRESHOLD = 15;

export class HeadTracker {
  private faceLandmarker: FaceLandmarker | null = null;
  private video: HTMLVideoElement | null = null;
  private running = false;
  private currentPose: HeadPose = { yaw: 0, pitch: 0, roll: 0, confidence: 0 };
  private lastNodTime = 0;
  private nodCooldown = 400;
  private action: HeadAction = {
    leanLeft: false,
    leanRight: false,
    nod: false,
    lookUp: false,
    lookDown: false,
  };

  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  async init(): Promise<boolean> {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );
      this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task',
          delegate: 'GPU',
        },
        outputFaceBlendshapes: false,
        runningMode: 'VIDEO',
        numFaces: 1,
      });

      this.video = document.createElement('video');
      this.video.style.display = 'none';
      document.body.appendChild(this.video);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
      });
      this.video.srcObject = stream;
      await this.video.play();

      this.running = true;
      this.detectLoop();
      return true;
    } catch (e) {
      console.warn('Camera access denied or unavailable:', e);
      return false;
    }
  }

  private detectLoop = () => {
    if (!this.running || !this.video || !this.faceLandmarker || !this.video.videoWidth) {
      if (this.running) requestAnimationFrame(this.detectLoop);
      return;
    }

    const results = this.faceLandmarker.detectForVideo(this.video, performance.now());
    if (results.faceLandmarks && results.faceLandmarks.length > 0) {
      const landmarks = results.faceLandmarks[0];
      this.currentPose = this.computeHeadPose(landmarks);
      this.action = this.computeAction(this.currentPose);
    } else {
      this.action = { leanLeft: false, leanRight: false, nod: false, lookUp: false, lookDown: false };
    }

    requestAnimationFrame(this.detectLoop);
  };

  private computeHeadPose(landmarks: any[]): HeadPose {
    const nose = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const leftEar = landmarks[234];
    const rightEar = landmarks[454];
    const leftMouth = landmarks[61];
    const rightMouth = landmarks[291];

    // Yaw: horizontal head turn - use nose position relative to eyes
    const faceWidth = Math.abs(rightEar.x - leftEar.x);
    const noseOffset = (nose.x - (leftEye.x + rightEye.x) / 2) / (faceWidth || 0.001);
    const yaw = noseOffset * 90;

    // Pitch: vertical tilt - use nose y relative to eye-mouth midpoint
    const eyeMidY = (leftEye.y + rightEye.y) / 2;
    const mouthMidY = (leftMouth.y + rightMouth.y) / 2;
    const faceHeight = Math.abs(mouthMidY - eyeMidY);
    const noseYOffset = (nose.y - eyeMidY) / (faceHeight || 0.001);
    const pitch = (noseYOffset - 0.5) * 120;

    // Roll: head tilt - use eye line angle
    const eyeDx = rightEye.x - leftEye.x;
    const eyeDy = rightEye.y - leftEye.y;
    const roll = Math.atan2(eyeDy, eyeDx) * (180 / Math.PI);

    return {
      yaw,
      pitch: -pitch,
      roll,
      confidence: 0.85,
    };
  }

  private computeAction(pose: HeadPose): HeadAction {
    const action: HeadAction = {
      leanLeft: false,
      leanRight: false,
      nod: false,
      lookUp: false,
      lookDown: false,
    };

    // Most front cameras don't mirror: left turn → negative yaw
    if (pose.yaw < -YAW_THRESHOLD) {
      action.leanLeft = true;
    } else if (pose.yaw > YAW_THRESHOLD) {
      action.leanRight = true;
    }

    const now = performance.now();
    if (pose.pitch > PITCH_UP_THRESHOLD) {
      if (now - this.lastNodTime > this.nodCooldown) {
        action.nod = true;
        this.lastNodTime = now;
      } else {
        action.lookUp = true;
      }
    }

    if (pose.pitch < -PITCH_DOWN_THRESHOLD) {
      action.lookDown = true;
    }

    return action;
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.video;
  }

  stop() {
    this.running = false;
    if (this.video) {
      const stream = this.video.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      this.video.remove();
      this.video = null;
    }
  }

  getAction(): HeadAction {
    return this.action;
  }

  getPose(): HeadPose {
    return this.currentPose;
  }

  isLeanLeft(): boolean {
    return this.action.leanLeft;
  }

  isLeanRight(): boolean {
    return this.action.leanRight;
  }

  hasNodded(): boolean {
    return this.action.nod;
  }

  isLookingUp(): boolean {
    return this.action.lookUp;
  }

  isLookingDown(): boolean {
    return this.action.lookDown;
  }
}
